import type { AssessmentResultV2 } from '../types';

export interface CompleteAssessmentDraftOptions {
  results: readonly AssessmentResultV2[];
  completeResult: (original: AssessmentResultV2, index: number) => Promise<AssessmentResultV2>;
  attachEvidence: (completed: AssessmentResultV2[]) => Promise<AssessmentResultV2 | null>;
  completeSession: () => Promise<void>;
  restoreSession?: () => Promise<void>;
  restoreResult: (original: AssessmentResultV2) => Promise<void>;
}

/** The original failure is retained if recovery itself cannot be completed. */
export class AssessmentDraftRollbackError extends Error {
  readonly cause: unknown;
  readonly rollbackErrors: readonly unknown[];

  constructor(cause: unknown, rollbackErrors: readonly unknown[]) {
    const detail = cause instanceof Error ? cause.message : String(cause);
    super(`A avaliação não foi concluída: ${detail}. A recuperação do rascunho ficou incompleta (${rollbackErrors.length} ${rollbackErrors.length === 1 ? 'falha' : 'falhas'}). Recarregue a sessão e confira os registros antes de continuar.`);
    this.name = 'AssessmentDraftRollbackError';
    this.cause = cause;
    this.rollbackErrors = rollbackErrors;
  }
}

/**
 * Complete the session only after its results and the clinical evidence are saved.
 * Original JSON snapshots are retained because evidence attachment may update a
 * completed clinical result before a later operation fails.
 */
export const completeAssessmentDraft = async ({
  results, completeResult, attachEvidence, completeSession, restoreSession, restoreResult,
}: CompleteAssessmentDraftOptions): Promise<{
  completedResults: AssessmentResultV2[];
  clinicalResult: AssessmentResultV2 | null;
}> => {
  const originals = results.map((result) => JSON.parse(JSON.stringify(result)) as AssessmentResultV2);
  const attempted: AssessmentResultV2[] = [];
  const completedResults: AssessmentResultV2[] = [];
  let sessionCompletionAttempted = false;
  try {
    for (const [index, original] of originals.entries()) {
      // A rejected network response may still correspond to a committed update.
      // Restoring the attempted result is safe and covers that ambiguous case.
      attempted.push(original);
      completedResults.push(await completeResult(JSON.parse(JSON.stringify(original)) as AssessmentResultV2, index));
    }
    const clinicalResult = await attachEvidence(completedResults);
    sessionCompletionAttempted = true;
    await completeSession();
    return { completedResults, clinicalResult };
  } catch (error: unknown) {
    const rollbackErrors: unknown[] = [];
    if (sessionCompletionAttempted && restoreSession) {
      try {
        await restoreSession();
      } catch (rollbackError: unknown) {
        rollbackErrors.push(rollbackError);
      }
    }
    // Reverse order restores the clinical result as well as results already
    // promoted. Every restoration is attempted, even if another one fails.
    for (const original of [...attempted].reverse()) {
      try {
        await restoreResult(original);
      } catch (rollbackError: unknown) {
        rollbackErrors.push(rollbackError);
      }
    }
    if (rollbackErrors.length) throw new AssessmentDraftRollbackError(error, rollbackErrors);
    throw error;
  }
};
